import type { ReactNode } from 'react'
import { fbt } from 'fbtee'

export interface ProfileStat {
  key: string
  value: number
  label: ReactNode
  onClick?: () => void
}

/**
 * Festivals and parties someone has been to (past "going"), the artists they
 * follow and — on your own profile — your friends. Only counts — the history list itself stays on the owner's profile.
 */
export function buildProfileStats({
  pastEvents,
  artistCount,
  friendCount,
  onEventsClick,
  onArtistsClick,
  onFriendsClick,
}: {
  pastEvents: { type?: string }[]
  artistCount: number
  /** Only on your own profile — someone else's friendships aren't readable. */
  friendCount?: number
  onEventsClick?: () => void
  onArtistsClick?: () => void
  onFriendsClick?: () => void
}): ProfileStat[] {
  const festivals = pastEvents.filter(e => e.type === 'festival').length
  const parties = pastEvents.length - festivals
  const stats: ProfileStat[] = [
    {
      key: 'festivals',
      value: festivals,
      label: fbt(fbt.plural('festival', festivals, { many: 'festivals' }), 'Profile stat label under the count of festivals the person has been to'),
      onClick: onEventsClick,
    },
    {
      key: 'parties',
      value: parties,
      label: fbt(fbt.plural('party', parties, { many: 'parties' }), 'Profile stat label under the count of parties the person has been to'),
      onClick: onEventsClick,
    },
    {
      key: 'artists',
      value: artistCount,
      label: fbt(fbt.plural('artist', artistCount, { many: 'artists' }), 'Profile stat label under the count of artists the person follows'),
      onClick: onArtistsClick,
    },
  ]
  if (friendCount !== undefined) {
    stats.push({
      key: 'friends',
      value: friendCount,
      label: fbt(fbt.plural('friend', friendCount, { many: 'friends' }), 'Profile stat label under the count of your friends'),
      onClick: onFriendsClick,
    })
  }
  return stats
}
